// Bible 14.4 / Guide §9.3 — PIN rules. Owner = 6 digits, Staff = 4 digits.
// Rejected: all-same digits, sequential digits (ascending or descending), and the 100 most common PINs.
import { bnToEnDigits } from './money';

export type PinKind = 'owner' | 'staff';
export const PIN_LENGTH: Record<PinKind, number> = { owner: 6, staff: 4 };

export type PinProblem = 'format' | 'length' | 'same_digits' | 'sequential' | 'common';

// 100 most common numeric PINs of length 4 and 6 (mixture). Source: public breach-frequency analyses.
export const COMMON_PINS: readonly string[] = [
  '1234', '1111', '0000', '1212', '7777', '1004', '2000', '4444', '2222', '6969',
  '9999', '3333', '5555', '6666', '1122', '1313', '8888', '4321', '2001', '1010',
  '0007', '1818', '1230', '2580', '1357', '0852', '2468', '1479', '1590', '9876',
  '123456', '111111', '000000', '121212', '654321', '666666', '123123', '112233', '159753', '789456',
  '007007', '222222', '555555', '123321', '696969', '987654', '131313', '147258', '246810', '101010',
  '999999', '333333', '777777', '888888', '444444', '102030', '112358', '121314', '135790', '147852',
  '159357', '321321', '456789', '520520', '789789', '852456', '951753', '963852', '741852', '010203',
  '1987', '1990', '1995', '1999', '2010', '2020', '2021', '2022', '2023', '2024',
  '2025', '2026', '1971', '1952', '1947', '0786', '0110', '1100', '2211', '3210',
  '0123', '1020', '5678', '6789', '4567', '3456', '2345', '1478', '3690', '0420',
];

const COMMON_SET = new Set(COMMON_PINS);

function isSequential(pin: string): boolean {
  let asc = true;
  let desc = true;
  for (let i = 1; i < pin.length; i++) {
    const diff = Number(pin[i]) - Number(pin[i - 1]);
    if (diff !== 1) asc = false;
    if (diff !== -1) desc = false;
  }
  return asc || desc;
}

/** Returns null if acceptable, otherwise the reason. Accepts Bangla digits. */
export function pinProblem(kind: PinKind, rawPin: string): PinProblem | null {
  const pin = bnToEnDigits(String(rawPin ?? ''));
  if (!/^\d+$/.test(pin)) return 'format';
  if (pin.length !== PIN_LENGTH[kind]) return 'length';
  if (/^(\d)\1+$/.test(pin)) return 'same_digits';
  if (isSequential(pin)) return 'sequential';
  if (COMMON_SET.has(pin)) return 'common';
  return null;
}

export const normalizePin = (raw: string): string => bnToEnDigits(String(raw ?? '')).trim();

export const PIN_PROBLEM_MESSAGE_BN: Record<PinProblem, string> = {
  format: 'PIN শুধু সংখ্যা হতে হবে।',
  length: 'PIN-এর দৈর্ঘ্য ঠিক নেই।',
  same_digits: 'সব অঙ্ক এক হলে PIN গ্রহণ হয় না।',
  sequential: 'ধারাবাহিক অঙ্কের PIN (যেমন ১২৩৪) গ্রহণ হয় না।',
  common: 'এই PIN খুব সাধারণ। অন্য একটা PIN দিন।',
};

export const PIN_WARNING_BN = 'bKash, Nagad বা ব্যাংকের PIN এখানে দেবেন না।';
export const PIN_WARNING_EN = 'Do not use your bKash, Nagad or bank PIN here.';
